import {useQuery} from '@apollo/client';
import * as React from 'react';
import {useNavigate, useParams} from 'react-router';

import {
  FeedComment,
  FeedPost,
  commentAuthorInitial,
  commentAuthorName,
  formatRelativeTime,
  postAuthorInitial,
  postAuthorName,
} from '../socialFeed';
import {QUERY_STUDENT_POST} from '../studentQueries';
import {StudentTheme} from '../studentTheme';

interface PostDetail extends FeedPost {
  comments?: {total?: number | null; list?: FeedComment[] | null} | null;
}

interface PostData {
  post?: PostDetail | null;
}

interface Props {
  theme: StudentTheme;
}

export const PostDetailScreen: React.FC<Props> = ({theme}) => {
  const {postId} = useParams();
  const navigate = useNavigate();

  const {data, loading, error, refetch} = useQuery<PostData>(QUERY_STUDENT_POST, {
    variables: {id: postId},
    skip: !postId,
  });

  const post = data?.post;
  const comentarios = post?.comments?.list ?? [];
  const quando = post ? formatRelativeTime(post.createdAt) : '';

  return (
    <div className="post-detalhe">
      <button
        type="button"
        className="wod-detalhe__voltar student-app__retry"
        style={{color: theme.primary}}
        onClick={() => navigate(-1)}
      >
        <i className="fa-light fa-chevron-left" aria-hidden="true" /> Voltar
      </button>

      {loading && <p className="student-screen__estado">Carregando…</p>}

      {error && !loading && (
        <div className="student-screen__estado">
          <p>Não foi possível carregar a publicação.</p>
          <button type="button" className="student-app__retry" onClick={() => refetch()}>
            Tentar de novo
          </button>
        </div>
      )}

      {!loading && !error && !post && <p className="student-screen__estado">Publicação não encontrada.</p>}

      {!loading && !error && post && (
        <>
          <article className="post-card" style={{backgroundColor: theme.body, borderColor: theme.border}}>
            <header className="post-card__header">
              {post.author?.avatar?.uri ? (
                <img className="post-card__avatar" src={post.author.avatar.uri} alt="" loading="lazy" />
              ) : (
                <span
                  className="post-card__avatar post-card__avatar--vazio"
                  style={{backgroundColor: theme.neutral, color: theme.background}}
                  aria-hidden="true"
                >
                  {postAuthorInitial(post)}
                </span>
              )}

              <div className="post-card__identidade">
                <span className="post-card__autor">{postAuthorName(post)}</span>
                {quando && (
                  <span className="post-card__quando" style={{color: theme.neutral}}>
                    {quando}
                  </span>
                )}
              </div>
            </header>

            {post.message && <p className="post-card__mensagem">{post.message}</p>}

            {post.image?.uri && <img className="post-card__imagem" src={post.image.uri} alt="" loading="lazy" />}

            <footer className="post-card__rodape" style={{color: theme.neutral}}>
              <span className="post-card__metrica">
                <i className={`${post.liked ? 'fa-solid' : 'fa-light'} fa-heart`} aria-hidden="true" />
                {post.likesTotal ?? 0}
              </span>
              <span className="post-card__metrica">
                <i className="fa-light fa-comment" aria-hidden="true" />
                {post.comments?.total ?? 0}
              </span>
            </footer>
          </article>

          <h2 className="post-detalhe__titulo">
            {comentarios.length === 1 ? '1 comentário' : `${comentarios.length} comentários`}
          </h2>

          {comentarios.length === 0 && (
            <p className="student-screen__estado" style={{color: theme.neutral}}>
              Nenhum comentário ainda.
            </p>
          )}

          <ul className="comentarios">
            {comentarios.map((comentario) => (
              <li
                key={comentario.id}
                className="comentario"
                style={{backgroundColor: theme.body, borderColor: theme.border}}
              >
                {comentario.author?.avatar?.uri ? (
                  <img className="post-card__avatar" src={comentario.author.avatar.uri} alt="" loading="lazy" />
                ) : (
                  <span
                    className="post-card__avatar post-card__avatar--vazio"
                    style={{backgroundColor: theme.neutral, color: theme.background}}
                    aria-hidden="true"
                  >
                    {commentAuthorInitial(comentario)}
                  </span>
                )}

                <div className="comentario__corpo">
                  <span className="post-card__autor">{commentAuthorName(comentario)}</span>
                  <p className="comentario__mensagem">{comentario.message}</p>
                </div>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
};
